import type { FunctionComponent } from 'react';
import { useEffect } from 'react';
import { useAdContext } from '../../../property-page/contexts/PropertyPageAdContextProvider/PropertyPageAdContextProvider';

const getSlotById = (id: string) =>
  googletag
    .pubads()
    .getSlots()
    .find((s) => s.getSlotElementId() === id);

export interface AdSlotConfig {
  // ID of the div that will contain this ad unit.
  id: string;
  // Path to the ad unit in the ad server
  path: string;
  // Size of the ad unit. This can be single size, multiple sizes or "fluid" for responsive ads.
  size: [number, number] | [number, number][] | 'fluid';
  //  Custom targeting parameters for this slot
  targeting?: Record<string, string[]>;
}

interface DefineAdSlotProps {
  slotConfig: AdSlotConfig;
}

export const DefineAdSlot: FunctionComponent<DefineAdSlotProps> = ({
  slotConfig: { path, size, id, targeting },
}) => {
  const { isPageLevelTargetingSet } = useAdContext();

  useEffect(() => {
    window.googletag?.cmd.push(() => {
      const matchingSlot = getSlotById(id);
      if (matchingSlot) return;
      if (!isPageLevelTargetingSet) return;

      const definedSlot = googletag.defineSlot(path, size, id);
      if (definedSlot) {
        if (targeting) {
          Object.entries(targeting).forEach(([key, value]) => {
            definedSlot.setTargeting(key, value);
          });
        }
        definedSlot.addService(window.googletag.pubads());
        window.googletag.display(definedSlot);
        window.googletag.pubads().refresh();
      }
    });

    return () => {
      window.googletag?.cmd.push(() => {
        const matchingSlot = getSlotById(id);
        if (matchingSlot) {
          window.googletag.destroySlots([matchingSlot]);
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPageLevelTargetingSet]);

  return <div id={id} />;
};
